/**
 * The template's own fields above the canvas: its name beside its key, and
 * a description across both. How it starts is the first node's (Feature 80
 * D1), set in its inspector.
 *
 * The key is made from the name while the template is new (Feature 73 D7,
 * D13), the next free one when another template holds it. It shows in a
 * read-only box with Edit inside, which turns the box into a field: typed by
 * hand, the key stops following the name. A saved template's key is fixed
 * (F71 D19) and its box has no Edit. The name keeps to the characters the
 * agent takes (D8), and says so when it does not.
 */
import { useEffect, useState } from 'react';

import { Button } from '../../ui/Button.js';
import { Field } from '../../ui/Field.js';
import { Grid, Input } from '../../ui/finixt.js';
import type { Draft } from './draft.js';
import { keyFromName, nameProblem } from './key.js';

const NAME_HINT = 'letters, digits, spaces, _ - . only';

/** The key's cell: a read-only box, with Edit inside while the template is new. */
function KeyCell({
  draft,
  isNew,
  editing,
  onEdit,
  onChange,
}: {
  draft: Draft;
  isNew: boolean;
  editing: boolean;
  onEdit: () => void;
  onChange: (draft: Draft) => void;
}) {
  if (isNew && editing) {
    return (
      <Field id="linkage-key" label="Key" hint="lowercase letters, digits and _; fixed once saved">
        <Input
          id="linkage-key"
          value={draft.key}
          maxLength={64}
          onChange={(e) => onChange({ ...draft, key: e.target.value })}
        />
      </Field>
    );
  }
  return (
    <Field label="Key" hint={isNew ? 'made from the name · fixed once saved' : 'fixed'}>
      <div
        style={{
          position: 'relative',
          boxSizing: 'border-box',
          padding: 'var(--pad-input)',
          // Room for Edit inside the box.
          ...(isNew ? { paddingRight: 64 } : {}),
          border: '1px solid var(--border-hairline)',
          borderRadius: 'var(--radius-input)',
          background: 'var(--surface-sunken)',
          fontSize: 'var(--text-body-md)',
        }}
      >
        <span
          className="mono"
          aria-label="Key"
          style={{ fontWeight: 'var(--weight-semibold)', overflowWrap: 'anywhere' }}
        >
          {draft.key || '—'}
        </span>
        {isNew && (
          <span
            style={{ position: 'absolute', right: 5, top: '50%', transform: 'translateY(-50%)' }}
          >
            <Button variant="ghost" size="sm" onClick={onEdit}>
              Edit
            </Button>
          </span>
        )}
      </div>
    </Field>
  );
}

export function LinkageFields({
  draft,
  isNew,
  takenKeys,
  onChange,
}: {
  draft: Draft;
  isNew: boolean;
  /** The keys other templates hold, so a made key is a free one. */
  takenKeys: ReadonlySet<string>;
  onChange: (draft: Draft) => void;
}) {
  const [editing, setEditing] = useState(false);
  const problem = draft.name ? nameProblem(draft.name) : null;
  const following = isNew && !editing;

  // The other templates' keys may arrive after the name is typed.
  useEffect(() => {
    if (!following || !draft.name) return;
    const key = keyFromName(draft.name, takenKeys);
    if (key !== draft.key) onChange({ ...draft, key });
  }, [following, takenKeys, draft, onChange]);

  return (
    <Grid
      columns={2}
      gap={14}
      style={{
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        columnGap: 22,
        alignItems: 'start',
      }}
    >
      <Field id="linkage-name" label="Name" required hint={NAME_HINT} error={problem ?? undefined}>
        <Input
          id="linkage-name"
          value={draft.name}
          maxLength={128}
          onChange={(e) => {
            const name = e.target.value;
            // A new template's key follows its name until it is typed by hand.
            const key = following ? keyFromName(name, takenKeys) : draft.key;
            onChange({ ...draft, name, key });
          }}
        />
      </Field>
      <KeyCell
        draft={draft}
        isNew={isNew}
        editing={editing}
        onEdit={() => setEditing(true)}
        onChange={onChange}
      />
      <Field id="linkage-description" label="Description" style={{ gridColumn: '1 / -1' }}>
        <Input
          id="linkage-description"
          value={draft.description ?? ''}
          onChange={(e) => onChange({ ...draft, description: e.target.value || null })}
        />
      </Field>
    </Grid>
  );
}
